import { AiInferenceOperation } from '../../api';
import { assertDemoAllowed } from '../auth/demo-mode.utils';

/**
 * The Testing tab talks to the LiteLLM gateway (OpenAI-compatible), NOT to the Enter API, using a key issued
 * in this session. Whether a browser at the support origin is allowed to call it (CORS) is unverified; a failed
 * fetch is reported as such rather than as a gateway answer.
 */

export type GatewayEndpointId = 'chat' | 'responses' | 'embeddings' | 'completions' | 'models';

export interface IGatewayEndpoint {
    id: GatewayEndpointId;
    label: string;
    /** Operation a key must hold to use it; null for GET /models, which any MODEL key may call. */
    operation: AiInferenceOperation | null;
    path: string;
    method: 'GET' | 'POST';
}

export const GATEWAY_ENDPOINTS: ReadonlyArray<IGatewayEndpoint> = [
    { id: 'chat', label: 'Chat completions', operation: AiInferenceOperation.CHATCOMPLETIONS, path: '/chat/completions', method: 'POST' },
    { id: 'responses', label: 'Responses', operation: AiInferenceOperation.RESPONSES, path: '/responses', method: 'POST' },
    { id: 'embeddings', label: 'Embeddings', operation: AiInferenceOperation.EMBEDDINGS, path: '/embeddings', method: 'POST' },
    { id: 'completions', label: 'Completions', operation: AiInferenceOperation.COMPLETIONS, path: '/completions', method: 'POST' },
    { id: 'models', label: 'List models', operation: null, path: '/models', method: 'GET' },
];

export interface IChatMessage {
    role: 'user' | 'assistant' | 'system';
    content: string;
}

export interface IGatewayRequest {
    endpoint: IGatewayEndpoint;
    model: string;
    input: string;
    system?: string;
    history?: IChatMessage[];
    temperature?: number;
    maxTokens?: number;
    stream?: boolean;
}

/** The endpoints a key may call: its operations, plus GET /models for MODEL keys. */
export function endpointsForKey(key: { operations: AiInferenceOperation[]; targetType: string }): IGatewayEndpoint[] {
    return GATEWAY_ENDPOINTS.filter(e =>
        e.operation ? key.operations.includes(e.operation) : key.targetType === 'MODEL',
    );
}

export function buildBody(req: IGatewayRequest): Record<string, unknown> | undefined {
    const { endpoint, model, input, system, history = [], temperature, maxTokens, stream } = req;
    if (endpoint.id === 'models') {
        return undefined;
    }
    const body: Record<string, unknown> = { model };
    switch (endpoint.id) {
        case 'chat':
            body.messages = [
                ...(system ? [{ role: 'system', content: system }] : []),
                ...history.map(m => ({ role: m.role, content: m.content })),
                { role: 'user', content: input },
            ];
            if (temperature !== undefined) body.temperature = temperature;
            if (maxTokens !== undefined) body.max_tokens = maxTokens;
            if (stream) body.stream = true;
            break;
        case 'responses':
            body.input = input;
            if (temperature !== undefined) body.temperature = temperature;
            if (maxTokens !== undefined) body.max_output_tokens = maxTokens;
            break;
        case 'embeddings':
            body.input = input;
            break;
        case 'completions':
            body.prompt = input;
            if (temperature !== undefined) body.temperature = temperature;
            if (maxTokens !== undefined) body.max_tokens = maxTokens;
            break;
    }
    return body;
}

/**
 * One word for a POSIX shell. Inside single quotes nothing is special except the quote itself, which is closed,
 * escaped and reopened (`don't` becomes `'don'\''t'`). Without this a prompt such as `don't` ends the quoted JSON
 * early, and a prompt or model name with a quote in it can run shell syntax when the copied command is pasted.
 */
const shellQuote = (value: string): string => `'${value.replace(/'/g, "'\\''")}'`;

/**
 * A cURL for the same request, safe to paste into a POSIX shell whatever was typed into it. Always references
 * `$ENTER_AI_KEY`; the real key is never written into it. The URL is quoted as well: the base URL comes from the
 * server, so it is data, not code.
 */
export function buildCurl(baseUrl: string, req: IGatewayRequest): string {
    const body = buildBody(req);
    return (
        `curl ${req.endpoint.method === 'GET' ? '' : '-X POST '}${shellQuote(`${baseUrl}${req.endpoint.path}`)} \\\n` +
        `  -H "Authorization: Bearer $ENTER_AI_KEY"` +
        (body ? ` \\\n  -H "Content-Type: application/json" \\\n  -d ${shellQuote(JSON.stringify(body, null, 2))}` : '')
    );
}

export interface IGatewayResult {
    status: number;
    ms: number;
    json: any;
    /** Text to show: assistant reply, output, completion, embedding summary or model list. */
    text: string;
    usage?: { input: number; output: number; total: number };
}

export function extractText(endpoint: GatewayEndpointId, json: any): string {
    switch (endpoint) {
        case 'chat':
            return json?.choices?.[0]?.message?.content ?? '';
        case 'responses':
            return (
                json?.output_text ??
                (json?.output ?? [])
                    .flatMap((o: any) => o.content ?? [])
                    .map((c: any) => c.text ?? '')
                    .join('')
            );
        case 'embeddings': {
            const v: number[] | undefined = json?.data?.[0]?.embedding;
            return v ? `${v.length} dimensions\n[${v.slice(0, 8).map(x => x.toFixed(5)).join(', ')}, …]` : '';
        }
        case 'completions':
            return json?.choices?.[0]?.text ?? '';
        case 'models':
            return (json?.data ?? []).map((m: any) => m.id).join('\n');
    }
}

export function extractUsage(json: any): IGatewayResult['usage'] {
    const u = json?.usage;
    if (!u) return undefined;
    const input = u.prompt_tokens ?? u.input_tokens ?? 0;
    const output = u.completion_tokens ?? u.output_tokens ?? 0;
    return { input, output, total: u.total_tokens ?? input + output };
}

/**
 * Incremental parser for a server-sent-events body. Feed it decoded chunks as they arrive; a chunk can end
 * mid-line, so the unfinished tail is carried to the next call. Returns the content deltas completed by this
 * chunk plus the last parsed JSON event (for usage) and whether `[DONE]` was seen.
 */
export class SseChatParser {
    private buffer = '';
    public last: any = undefined;
    public done = false;
    /**
     * Set when the stream carried an error event (`data: {"error": …}`). The HTTP status was already sent as 200 by then,
     * so this is the only signal that the reply is a failure and not a short answer. The caller must check it after each
     * `push` and fail; reading stops at the event.
     */
    public error: GatewayError | undefined = undefined;

    public push(chunk: string): string[] {
        this.buffer += chunk;
        const lines = this.buffer.split('\n');
        this.buffer = lines.pop() ?? '';
        const deltas: string[] = [];
        for (const raw of lines) {
            const line = raw.trim();
            if (!line.startsWith('data:')) continue;
            const payload = line.slice(5).trim();
            if (payload === '[DONE]') {
                this.done = true;
                continue;
            }
            let json: any;
            try {
                json = JSON.parse(payload);
            } catch {
                // A malformed event is skipped, not fatal: the stream may still carry good ones.
                continue;
            }
            this.last = json;
            // Outside the try above on purpose: an error event is well-formed JSON and must not be swallowed with the
            // malformed ones.
            const failure = gatewayErrorFrom(json);
            if (failure) {
                this.error = failure;
                break;
            }
            const delta = json?.choices?.[0]?.delta?.content;
            if (typeof delta === 'string' && delta) deltas.push(delta);
        }
        return deltas;
    }
}

export class GatewayError extends Error {
    constructor(message: string, public readonly status?: number, public readonly json?: any) {
        super(message);
    }
}

/**
 * The failure carried in a body, if it carries one: OpenAI-compatible gateways report an upstream problem as
 * `{"error": {"message", "code"}}` (or a bare string), and may do so after already answering 200, in a JSON reply or
 * in the middle of a stream. `"error": null`, which some replies include, is not a failure.
 */
function gatewayErrorFrom(json: any): GatewayError | undefined {
    const er = json && typeof json === 'object' ? json.error : undefined;
    if (!er) return undefined;
    const message = typeof er === 'string' ? er : er.message || 'The gateway reported an error.';
    const rawCode = typeof er === 'object' ? er.code ?? er.status_code : undefined;
    const numeric = typeof rawCode === 'number' ? rawCode : typeof rawCode === 'string' && rawCode.trim() !== '' ? Number(rawCode) : NaN;
    const status = Number.isFinite(numeric) ? numeric : undefined;
    return new GatewayError(`Gateway error${status ? ` ${status}` : ''}: ${message}`, status, json);
}

export interface IGatewayCall {
    baseUrl: string;
    apiKey: string;
    request: IGatewayRequest;
    signal?: AbortSignal;
    /** Called with the full text so far while a stream is in progress. */
    onText?: (textSoFar: string) => void;
}

/**
 * Raw `fetch` bypasses the API-layer demo guard, so the same predicate is called here (NS-14). In demo mode this
 * throws before any request leaves the browser.
 */
export async function callGateway({ baseUrl, apiKey, request, signal, onText }: IGatewayCall): Promise<IGatewayResult> {
    const url = baseUrl + request.endpoint.path;
    assertDemoAllowed(url, request.endpoint.method);
    const body = buildBody(request);
    const t0 = performance.now();
    let res: Response;
    try {
        res = await fetch(url, {
            method: request.endpoint.method,
            headers: { Authorization: `Bearer ${apiKey}`, ...(body ? { 'Content-Type': 'application/json' } : {}) },
            body: body ? JSON.stringify(body) : undefined,
            signal,
        });
    } catch (e) {
        if ((e as Error)?.name === 'AbortError') throw e;
        throw new GatewayError(
            `Network error: ${(e as Error).message}. The gateway may be unreachable or may not allow browser requests (CORS) from this origin.`,
        );
    }

    // Only a response that says it is an event stream goes to the SSE parser. A gateway or proxy may answer a streaming
    // request with plain JSON (an error body, or a non-streamed reply); that is read as JSON below.
    if (
        body?.stream &&
        res.ok &&
        res.body &&
        res.headers.get('content-type')?.toLowerCase().includes('text/event-stream')
    ) {
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        const parser = new SseChatParser();
        let text = '';
        for (;;) {
            const { done, value } = await reader.read();
            if (done) break;
            const deltas = parser.push(decoder.decode(value, { stream: true }));
            if (deltas.length) {
                text += deltas.join('');
                onText?.(text);
            }
            if (parser.error) {
                // The reply failed after the headers said 200. What arrived has been shown; do not report a success.
                void reader.cancel().catch(() => undefined);
                throw parser.error;
            }
            if (parser.done) {
                // [DONE] is the end of the reply. A server that keeps the connection open after it must not keep the
                // console busy.
                void reader.cancel().catch(() => undefined);
                break;
            }
        }
        if (!parser.done) {
            // The connection closed before [DONE]: what arrived has been shown, but it is not the whole reply.
            throw new GatewayError('The stream ended before the gateway finished the reply, so the response is incomplete.');
        }
        return {
            status: res.status,
            ms: Math.round(performance.now() - t0),
            json: parser.last ?? { streamed: true },
            text,
            usage: extractUsage(parser.last),
        };
    }

    let json: any;
    try {
        json = await res.json();
    } catch (e) {
        // Clear or a key change aborts the request while its body is still being read. That is a cancellation, not an
        // empty reply: let the caller's handler see it, or it would restore the old messages into a console the
        // operator just cleared or switched.
        if ((e as Error)?.name === 'AbortError') throw e;
        json = null;
    }
    if (!res.ok) {
        const er = json?.error;
        throw new GatewayError(
            `${res.status} ${typeof er === 'string' ? er : er?.message || res.statusText || 'Request failed'}`,
            res.status,
            json,
        );
    }
    if (json === null) {
        throw new GatewayError('The gateway answered but the reply is not valid JSON.', res.status);
    }
    const failure = gatewayErrorFrom(json);
    if (failure) throw failure;
    return {
        status: res.status,
        ms: Math.round(performance.now() - t0),
        json,
        text: extractText(request.endpoint.id, json),
        usage: extractUsage(json),
    };
}
